import * as React from 'react';
import { Dispatch } from 'react-redux';
import { I18n } from 'react-redux-i18n';

import { StoreState } from '../../../reducers';
import ProposalTemplates from '../../../containers/ProposalCreator/ProposalTemplates';
import ResourceCreationPanel from '../../ResourceCreationPanel';
import Popup from '../../Popup';
import ProgressBar from '../../ResourceSelector/ProgressBar';
import { PopupBodyWrapper } from '../styles';
import {
	hideProposalCreator,
	createProposalFromMasterProposal,
	createProposalFromDriveItem,
	selectTemplate,
	setName,
	removeFromCreationProgress,
} from '../../../containers/ProposalCreator/actions';
import { ProposalSource, SelectedTemplate, ProposalCreationStatus } from '../../../containers/ProposalCreator/reducer';
import { FileError } from '../../../containers/ImportManager/actions';
import { Api } from '../../../typings/api';

interface FromTemplateProps {
	isDisplayed: boolean;
	source: ProposalSource | null;
	selectedTemplate: SelectedTemplate | null;
	name: string;
	error: string | null;
	isImportingFile: boolean;
	importingStatus: ProposalCreationStatus;
	importingPercentage: number;
	importingError: FileError | null;
	dispatch: Dispatch<StoreState>;
}

const FromTemplate = ({
	isDisplayed,
	source,
	selectedTemplate,
	name,
	error,
	isImportingFile,
	importingStatus,
	importingPercentage,
	importingError,
	dispatch,
}: FromTemplateProps) => {
	const isFromTemplate = source === ProposalSource.TEMPLATE;
	const [isTemplatesDisplayed, setIsTemplatesDisplayed] = React.useState(isFromTemplate);

	React.useEffect(() => {
		setIsTemplatesDisplayed(isFromTemplate && isDisplayed);
	}, [isDisplayed]);

	const handleTemplateSelect = (data: SelectedTemplate) => {
		dispatch(selectTemplate(data));
		setIsTemplatesDisplayed(false);
	};

	const handleProposalNameConfirm = (name: string, selectedTemplate: SelectedTemplate) => {
		const { isCustomTemplate, id } = selectedTemplate;

		if (isCustomTemplate) {
			dispatch(createProposalFromDriveItem({ id, label: name } as Api.DriveItem, id.toString()));
		} else {
			dispatch(createProposalFromMasterProposal(id, name));
		}
	};

	const handleProposalNameCancel = () => {
		if (isFromTemplate) {
			setIsTemplatesDisplayed(true);
		} else {
			dispatch(hideProposalCreator());
		}
	};

	const getProgressBarLabel = () => (
		importingStatus
			? `messages.${importingStatus.toLowerCase()}`
			: 'messages.create_proposal'
	);

	const handleProposalCreationCancel = () => {
		dispatch(removeFromCreationProgress());
		setIsTemplatesDisplayed(true);
	};

	return (
		<>
			{/* in case creation proposal from template <ProposalTemplates />
				has to be always visible to avoid blicking when <ResourceCreationPanel /> is appearing
			*/}
			{(isFromTemplate ? isDisplayed : isTemplatesDisplayed) &&
				<ProposalTemplates
					onSelect={handleTemplateSelect}
					onClose={() => dispatch(hideProposalCreator())}
				/>
			}
			<Popup
				isDisplayed={isImportingFile && isDisplayed}
				label={I18n.t(getProgressBarLabel())}
				onCancel={handleProposalCreationCancel}
			>
				<PopupBodyWrapper>
					<ProgressBar
						percentage={importingPercentage}
						error={importingError}
						hasCancelButton={true}
						onCancel={handleProposalCreationCancel}
					/>
				</PopupBodyWrapper>
			</Popup>
			<ResourceCreationPanel
				label="messages.proposal_name"
				isOpen={!isImportingFile && isDisplayed && !isTemplatesDisplayed}
				autoFocus={false}
				icon="iconset_uprez_proposal-blue"
				fields={{ name: { value: name } }}
				errors={{ name: error }}
				onFieldChange={(name, value) => dispatch(setName(value))}
				onConfirm={({ name }) => handleProposalNameConfirm(name.value, selectedTemplate as SelectedTemplate)}
				onCancel={handleProposalNameCancel}
			/>
		</>
	);
};

export default FromTemplate;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ProposalCreator/FromTemplate/index.tsx